/**
 * <quick-add-modal> - global "quick add" modal showing a lightweight mini PDP.
 *
 * Opened from a product card's quick-add button. Fetches the product's
 * `product.quick-add` alternate template (rendered with `{% layout none %}`,
 * so the response is just the mini-PDP markup) and injects it into a native
 * <dialog> — same modal pattern as cart-drawer / search-modal.
 *
 * The injected markup contains <product-form-component> + <variant-selector>;
 * both upgrade automatically because their scripts are loaded globally by
 * sections/quick-add-modal.liquid. So add-to-cart and variant selection work
 * inside the modal with no extra wiring.
 *
 * When add-to-cart succeeds, CartAddEvent fires → the cart drawer opens, so we
 * close this modal to step out of the way.
 *
 * Public API:
 *   <quick-add-modal>
 *     .open(productUrl)  - fetch the mini PDP and show the dialog
 *     .close()           - close the dialog
 *
 * @typedef {Object} QuickAddModalRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} content
 *
 * @extends {Component<QuickAddModalRefs>}
 */

import { Component } from "@theme/component";
import { CartAddEvent } from "@theme/events";
import { formatMoney } from "@theme/money-formatting";

class QuickAddModal extends Component {
  requiredRefs = ["dialog", "content"];

  /** @type {AbortController|null} */
  #abortController = null;

  /** @type {AbortController|null} */
  #pageAbort = null;

  /** Cache of fetched partial HTML, keyed by product pathname. */
  #cache = new Map();

  /** Cache of fetched full product-page HTML (accordions), keyed by pathname. */
  #pageCache = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener("click", this.#handleBackdropClick);
    this.refs.dialog.addEventListener("close", this.#handleDialogClose);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener("variant:change", this.#handleVariantChange);
    this.addEventListener("click", this.#handlePairedClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#pageAbort?.abort();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  /**
   * Fetches the mini-PDP partial for the given product and shows the dialog.
   * @param {string} productUrl - canonical product URL (e.g. /products/handle)
   */
  async open(productUrl) {
    if (!productUrl) return;

    this.refs.content.replaceChildren();
    this.#setLoading(true);
    if (!this.refs.dialog.open) this.refs.dialog.showModal();

    try {
      const html = await this.#fetchPartial(productUrl);

      // The user may have closed the dialog while we were fetching.
      if (!this.refs.dialog.open) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const pdp = doc.querySelector(".quick-add-pdp");
      if (!pdp) throw new Error("quick-add partial markup not found");

      // Setting innerHTML on a connected node upgrades the custom elements
      // (<product-form-component>, <variant-selector>) in the main registry.
      this.refs.content.innerHTML = pdp.outerHTML;
      this.#setLoading(false);

      // Accordions live in the real product section's block settings (not in
      // the light template), so we fetch them separately and inject them.
      this.#loadAccordions(productUrl);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("[quick-add] open failed:", error);
      // Graceful fallback: send the customer to the full product page.
      window.location.href = productUrl;
    }
  }

  /** Closes the dialog (content is cleared by the dialog `close` listener). */
  close() {
    if (this.refs.dialog.open) this.refs.dialog.close();
  }

  /**
   * @param {string} productUrl
   * @returns {Promise<string>} the partial HTML
   */
  async #fetchPartial(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.search = "";
    url.hash = "";
    const key = url.pathname;

    const cached = this.#cache.get(key);
    if (cached) return cached;

    url.searchParams.set("view", "quick-add");

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const response = await fetch(url.toString(), { signal: this.#abortController.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = await response.text();
    this.#cache.set(key, html);
    return html;
  }

  /**
   * Fetches the full product page HTML. The accordions live in the product
   * section's block settings, so we read them from the real rendered page —
   * the lightweight `?view=quick-add` template can't expose block settings,
   * and `?section_id=main` is unreliable across alternate product templates.
   * @param {string} productUrl
   * @returns {Promise<string>} the product page HTML
   */
  async #fetchProductPage(productUrl) {
    const url = new URL(productUrl, window.location.origin);
    url.search = "";
    url.hash = "";
    const key = url.pathname;

    const cached = this.#pageCache.get(key);
    if (cached) return cached;

    this.#pageAbort?.abort();
    this.#pageAbort = new AbortController();

    const response = await fetch(url.toString(), { signal: this.#pageAbort.signal });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const html = await response.text();
    this.#pageCache.set(key, html);
    return html;
  }

  /**
   * Pulls the `.product-accordion` blocks from the real product section and
   * injects them into the mini PDP placeholder. They upgrade automatically
   * (<accordion-component> is registered globally).
   * @param {string} productUrl
   */
  async #loadAccordions(productUrl) {
    const slot = this.refs.content.querySelector("[data-quick-add-accordions]");
    if (!slot) return;

    try {
      const html = await this.#fetchProductPage(productUrl);
      // A newer open() (e.g. color switch) may have replaced the content.
      if (!slot.isConnected) return;

      const doc = new DOMParser().parseFromString(html, "text/html");
      const accordions = doc.querySelectorAll(".product-accordion");
      if (!accordions.length) {
        console.warn("[quick-add] no .product-accordion found on", productUrl);
        return;
      }

      slot.innerHTML = Array.from(accordions, (node) => {
        // Les accordéons « dispo boutique » arrivent ouverts (data-open="true")
        // depuis le drawer de la PDP ; on les replie par défaut dans la modal.
        if (node.classList.contains("boutique")) {
          node.setAttribute("data-open", "false");
          node.querySelector(".accordion__trigger")?.setAttribute("aria-expanded", "false");
        }
        return node.outerHTML;
      }).join("");
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("[quick-add] accordions load failed:", error);
    }
  }

  /** @param {boolean} state */
  #setLoading(state) {
    this.refs.dialog.toggleAttribute("data-loading", state);
  }

  /**
   * Native <dialog> doesn't close on backdrop click. Detect clicks landing on
   * the dialog element itself (the backdrop area), not its inner box.
   * @param {MouseEvent} event
   */
  #handleBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  /** Frees the injected DOM whenever the dialog closes (button, Esc, backdrop). */
  #handleDialogClose = () => {
    this.refs.content.replaceChildren();
    this.#setLoading(false);
  };

  /** A successful add-to-cart opens the cart drawer; get out of its way. */
  #handleCartAdd = () => {
    this.close();
  };

  /**
   * Clicking a linked-color swatch swaps the mini PDP in place rather than
   * navigating to the full product page — the customer stays in the modal.
   * @param {MouseEvent} event
   */
  #handlePairedClick = (event) => {
    const link = event.target.closest?.(".paired_product_link");
    if (!link) return;

    event.preventDefault();
    // Already the displayed color → nothing to do.
    if (link.classList.contains("active")) return;

    const href = link.getAttribute("href");
    if (href) this.open(href);
  };

  /**
   * Refresh price + add-to-cart button on variant change — client-side, no
   * server round-trip. <variant-selector> bubbles `variant:change` with the
   * matched variant (even when it's out of stock → available: false), so we
   * can disable the button and swap its label instead of relying on the PDP's
   * section re-render (which is intentionally skipped inside the modal).
   * @param {CustomEvent} event
   */
  #handleVariantChange = (event) => {
    const variant = event.detail?.variant;
    if (!variant) return;

    const priceEl = this.refs.content.querySelector(".quick-add-pdp__price .price_current");
    const format = this.dataset.moneyFormat;
    const currency = this.dataset.currency;
    if (priceEl && format && currency) {
      priceEl.textContent = formatMoney(variant.price, format, currency);
    }

    const submit = this.refs.content.querySelector(".product-form__submit");
    if (submit) {
      submit.disabled = !variant.available;
      const label = variant.available ? this.dataset.textAddToCart : this.dataset.textSoldOut;
      if (label) submit.textContent = label;
    }

    // Message "stock bas" : le quick-add ne re-rend pas la section, donc on
    // toggle l'attribut `hidden` du message en lisant l'inventaire de la
    // variante (présent dans le JSON data-variants). Seuil lu sur le conteneur.
    const stockWrap = this.refs.content.querySelector(".product_info_stock");
    const stockMsg = stockWrap?.querySelector(".product_info_stock_message");
    if (stockWrap && stockMsg) {
      const threshold = parseInt(stockWrap.dataset.lowStockThreshold, 10);
      const qty = variant.inventory_quantity;
      const isLowStock =
        Boolean(variant.inventory_management) &&
        typeof qty === "number" &&
        qty > 0 &&
        Number.isFinite(threshold) &&
        qty <= threshold;
      stockMsg.toggleAttribute("hidden", !isLowStock);
    }
  };
}

if (!customElements.get("quick-add-modal")) {
  customElements.define("quick-add-modal", QuickAddModal);
}
